// src/pages/users/UserRolesPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Plus, 
  Edit, 
  Trash2,
  Copy
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import { getUserRoles, deleteUserRole, duplicateUserRole } from '../../services/api/users';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './UserRolesPage.module.css';

const UserRolesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: 'name',
    orderDirection: 'ASC'
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: ''
  });

  useEffect(() => {
    fetchRoles();
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters]);

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);
      const response = await getUserRoles();
      
      // Apply filters locally
      let filteredRoles = response;
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredRoles = filteredRoles.filter(role =>
          role.name.toLowerCase().includes(searchLower) ||
          (role.remarks && role.remarks.toLowerCase().includes(searchLower))
        );
      }

      // Apply sorting
      filteredRoles.sort((a, b) => {
        const aVal = a[sorting.orderBy] || '';
        const bVal = b[sorting.orderBy] || '';
        const comparison = aVal.toString().localeCompare(bVal.toString());
        return sorting.orderDirection === 'ASC' ? comparison : -comparison;
      });

      // Update total
      const total = filteredRoles.length;

      // Apply pagination
      const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
      const paginatedRoles = filteredRoles.slice(startIndex, startIndex + pagination.pageSize);

      setRoles(paginatedRoles);
      setPagination(prev => ({ ...prev, total }));
    } catch (err) {
      console.error('Failed to fetch user roles:', err);
      setLoadingError(t('errors.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return filters.search !== '';
  };

  const handleDuplicateRole = async (roleId, roleName) => {
    const confirmed = await confirm(
      t('userRoles.confirmDuplicate', { roleName }),
      {
        confirmText: t('common.duplicate'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await duplicateUserRole(roleId);
      success(t('userRoles.roleDuplicatedSuccess', { roleName }));
      
      // Navigate to edit page of the newly created role
      if (response.role && response.role.id) {
        navigate(`/users/roles/${response.role.id}/edit`);
      } else {
        // Fallback: refresh the list
        fetchRoles();
      }
    } catch (err) {
      console.error('Failed to duplicate role:', err);
      error(err.response?.data?.message || t('userRoles.failedToDuplicate'));
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    const confirmed = await confirm(
      t('userRoles.confirmDelete', { roleName }),
      {
        confirmText: t('common.delete'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteUserRole(roleId);
      success(t('userRoles.roleDeletedSuccess', { roleName }));
      fetchRoles();
    } catch (err) {
      console.error('Failed to delete role:', err);
      // Show backend error message if available
      const errorMessage = err.response?.data?.message || t('userRoles.failedToDelete');
      error(errorMessage);
    }
  };

  // Define table columns
  const columns = [
    {
      key: 'name',
      header: t('userRoles.roleName'),
      sortable: true,
      width: '35%',
      render: (row) => (
        <div className={styles.roleNameWrapper}>
          <Shield className={styles.roleIcon} size={16} />
          <span className={styles.roleName}>{row.name}</span>
          {row.businessId === null && (
            <span className={styles.systemRoleBadge}>
              {t('userRoles.systemRole')}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'remarks',
      header: t('userRoles.description'),
      width: '50%',
      render: (row) => (
        <span className={styles.roleRemarks}>
          {row.remarks || t('userRoles.noDescription')}
        </span>
      )
    }
  ];

  // Define table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionsWrapper}>
        {/* Duplicate button - visible for ALL roles */}
        {hasPermission('create_user_role') && (
          <button
            className={`${styles.actionButton} ${styles.duplicateButton}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicateRole(row.id, row.name);
            }}
            title={t('common.duplicate')}
          >
            <Copy size={14} />
          </button>
        )}

        {/* Edit button - only for business-specific roles */}
        {hasPermission('edit_user_role') && row.businessId !== null && (
          <Link 
            to={`/users/roles/${row.id}/edit`}
            className={`${styles.actionButton} ${styles.editButton}`}
            title={t('common.edit')}
          >
            <Edit size={14} />
          </Link>
        )}
        
        {/* Delete button - only for business-specific roles */}
        {hasPermission('edit_user_role') && row.businessId !== null && (
          <button
            className={`${styles.actionButton} ${styles.deleteButton}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteRole(row.id, row.name);
            }}
            title={t('common.delete')}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    ),
    width: '15%',
    frozen: true
  };

  return (
    <div className={styles.userRolesPage}>
      {/* Page Header */}
      <PageHeader
        title={t('sidebar.userRoles')}
        description={t('userRoles.manageUserRoles')}
        icon={Shield}
        actions={
          hasPermission('create_user_role') && (
            <Link to="/users/roles/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('userRoles.createRole')}
            </Link>
          )
        }
      />

      {/* Search and Filters Row */}
      <FilterPanel
        filters={[]}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('userRoles.searchRoles')
        }}
      />

      {/* Data Table */}
      <DataTable
        data={roles}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => row.businessId !== null && navigate(`/users/roles/${row.id}/edit`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        error={loadingError}
        emptyMessage={
          hasActiveFilters()
            ? t('userRoles.noSearchResults')
            : t('userRoles.noRoles')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('userRoles.noSearchResultsDesc')
            : t('userRoles.noRolesDesc')
        }
        emptyIcon={<Shield size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearSearch')}
            </button>
          ) : hasPermission('create_user_role') ? (
            <Link to="/users/roles/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('userRoles.createFirstRole')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default UserRolesPage;